import React, { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Camera, Upload, X, StopCircle } from 'lucide-react';

const VideoInput = ({ onVideoSelect }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [showWebcam, setShowWebcam] = useState(false);
    const webcamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const [recordedChunks, setRecordedChunks] = useState([]);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            onVideoSelect(url);
        }
    };

    const handleStartRecording = useCallback(() => {
        setIsRecording(true);
        setRecordedChunks([]);

        // Check if webcam reference is ready
        if (webcamRef.current && webcamRef.current.stream) {
            mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
                mimeType: "video/webm"
            });
            mediaRecorderRef.current.addEventListener(
                "dataavailable",
                handleDataAvailable
            );
            mediaRecorderRef.current.start();
        }
    }, [webcamRef]);

    const handleDataAvailable = useCallback(
        ({ data }) => {
            if (data.size > 0) {
                setRecordedChunks((prev) => prev.concat(data));
            }
        },
        [setRecordedChunks]
    );

    const handleStopRecording = useCallback(() => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            // Wait a slight delay for the final chunk
            setTimeout(() => {
                setIsRecording(false);
                setShowWebcam(false);
                // The chunks are processed in a useEffect or we can manually finalize here if we had access to the state immediately. 
                // Ideally we wait for the state to update but for simplicity let's assume successful gather after stop.
            }, 500);
        }
    }, [mediaRecorderRef]);

    // Effect to finalize recording when chunks are ready and recording stopped
    React.useEffect(() => {
        if (!isRecording && recordedChunks.length > 0) {
            const blob = new Blob(recordedChunks, {
                type: "video/webm"
            });
            const url = URL.createObjectURL(blob);
            onVideoSelect(url);
        }
    }, [isRecording, recordedChunks, onVideoSelect]);


    if (showWebcam) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-black relative">
                <Webcam
                    audio={false}
                    ref={webcamRef}
                    className="h-full w-full object-contain"
                />
                <div className="absolute bottom-8 flex gap-4 bg-black/50 p-4 rounded-full backdrop-blur-sm">
                    {!isRecording ? (
                        <button
                            onClick={handleStartRecording}
                            className="w-16 h-16 bg-red-600 rounded-full border-4 border-white flex items-center justify-center hover:scale-110 transition-transform"
                        />
                    ) : (
                        <button
                            onClick={handleStopRecording}
                            className="w-16 h-16 bg-transparent border-4 border-red-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                        >
                            <div className="w-6 h-6 bg-red-500 rounded-sm" />
                        </button>
                    )}
                    <button
                        onClick={() => setShowWebcam(false)}
                        className="absolute -top-12 right-0 p-2 text-white bg-gray-800 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <h2 className="text-3xl font-bold mb-2 text-white">Upload or Record</h2>
            <p className="text-gray-400 mb-8">Get your delivery or shot into the lab</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
                <div className="relative group">
                    <input
                        type="file"
                        accept="video/*"
                        onChange={handleFileUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-blue-500/50 p-8 rounded-2xl transition-all duration-300 flex flex-col items-center h-full">
                        <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <Upload className="text-blue-500 w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Upload Video</h3>
                        <p className="text-sm text-gray-400">Import an existing video file (120fps recommended).</p>
                    </div>
                </div>

                <button
                    onClick={() => setShowWebcam(true)}
                    className="group bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-red-500/50 p-8 rounded-2xl transition-all duration-300 flex flex-col items-center h-full"
                >
                    <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Camera className="text-red-500 w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Record Now</h3>
                    <p className="text-sm text-gray-400">Capture directly from your camera.</p>
                </button>
            </div>
        </div>
    );
};

export default VideoInput;
